import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { Session } from '@supabase/supabase-js';

export interface AuthUser {
  session: Session | null;
  isAdmin: boolean;
  loading: boolean;
  employeeId: string | null;
}

export function useAuth() {
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const checkAdminStatus = async (email: string | undefined) => {
    if (!email) {
      setIsAdmin(false);
      setEmployeeId(null);
      setLoading(false);
      return;
    }

    const timeout = new Promise<void>((_, reject) =>
      setTimeout(() => reject(new Error('Admin check timeout')), 5000)
    );

    try {
      // Default admin check
      if (email === 'rollins.jeremy@gmail.com') {
        setIsAdmin(true);

        try {
          const query = supabase
            .from('employees')
            .select('id')
            .eq('email', email)
            .maybeSingle();

          const { data } = await Promise.race([query, timeout]);
          setEmployeeId(data?.id || null);
        } catch (err) {
          console.error('Error fetching employee ID:', err);
          setEmployeeId(null);
        }

        setLoading(false);
        return;
      }

      const query = supabase
        .from('employees')
        .select('id, is_admin')
        .eq('email', email)
        .maybeSingle();

      const { data, error } = await Promise.race([query, timeout]);

      if (error) throw error;
      setIsAdmin(!!data?.is_admin);
      setEmployeeId(data?.id || null);
    } catch (err) {
      console.error('Error checking admin status:', err);
      setIsAdmin(false);
      setEmployeeId(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      const timeout = setTimeout(() => {
        if (mounted) {
          console.error('Auth initialization timeout');
          setLoading(false);
        }
      }, 10000);

      try {
        const sessionTimeout = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Session timeout')), 5000)
        );

        const sessionPromise = supabase.auth.getSession();
        const { data: { session } } = await Promise.race([sessionPromise, sessionTimeout]);

        if (!mounted) return;

        setSession(session);
        await checkAdminStatus(session?.user?.email);
        clearTimeout(timeout);
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (mounted) {
          setLoading(false);
        }
        clearTimeout(timeout);
      }
    };

    initializeAuth();

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!mounted) return;

      if (event === 'SIGNED_OUT') {
        setSession(null);
        setIsAdmin(false);
        setEmployeeId(null);
        setLoading(false);
        return;
      }

      setSession(session);
      await checkAdminStatus(session?.user?.email);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  return { session, isAdmin, loading, employeeId, user: { employeeId } };
}